import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { User as FirebaseUser } from 'firebase/auth';
import {
	signUpWithEmail,
	signInWithEmail,
	signInWithGoogle,
	signInWithApple,
	handleGoogleRedirect,
	logOut,
	onAuthStateChange,
	resetPassword as firebaseResetPassword
} from '../lib/firebase';
import { API_URL, getAuthHeaders } from '../services/api';

const JWT_STORAGE_KEY = 'cloud_jwt';

interface User {
	uid: string;
	email: string | null;
	displayName: string | null;
	photoURL: string | null;
}

interface AuthContextType {
	user: User | null;
	firebaseUser: FirebaseUser | null;
	cloudToken: string | null;
	login: (email: string, password: string) => Promise<void>;
	loginWithGoogle: () => Promise<void>;
	loginWithApple: () => Promise<void>;
	loginWithAppleToken: (appleUser: { sub: string; email: string; name?: string; idToken: string }) => Promise<void>;
	register: (email: string, password: string, teamName?: string) => Promise<void>;
	resetPassword: (email: string) => Promise<void>;
	logout: () => Promise<void>;
	isAuthenticated: boolean;
	isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
	const [user, setUser] = useState<User | null>(null);
	const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
	const [cloudToken, setCloudToken] = useState<string | null>(() => localStorage.getItem(JWT_STORAGE_KEY));
	const [isLoading, setIsLoading] = useState(true);
	const pendingTeamNameRef = useRef<string | undefined>(undefined);

	// Sync user to cloud backend and store the returned JWT
	const syncUserToCloud = async (fbUser: FirebaseUser, teamName?: string) => {
		const idToken = await fbUser.getIdToken();
		const response = await fetch(`${API_URL}/api/v1/auth/sync`, {
			method: 'POST',
			headers: getAuthHeaders(idToken),
			body: JSON.stringify({
				uid: fbUser.uid,
				email: fbUser.email,
				display_name: fbUser.displayName || undefined,
				photo_url: fbUser.photoURL || undefined,
				team_name: teamName || undefined,
			}),
		});

		if (!response.ok) {
			const text = await response.text();
			throw new Error(`Sync failed (${response.status}): ${text}`);
		}

		const data = await response.json();
		if (data.token) {
			localStorage.setItem(JWT_STORAGE_KEY, data.token);
			setCloudToken(data.token);
		}
		return data;
	};

	// Process Google redirect result on mount
	useEffect(() => {
		handleGoogleRedirect();
	}, []);

	// Listen to Firebase auth state changes — no deps that cause resubscription
	useEffect(() => {
		const unsubscribe = onAuthStateChange(async (firebaseUser) => {
			if (firebaseUser) {
				const userData: User = {
					uid: firebaseUser.uid,
					email: firebaseUser.email,
					displayName: firebaseUser.displayName,
					photoURL: firebaseUser.photoURL,
				};
				setUser(userData);
				setFirebaseUser(firebaseUser);

				// Sync user to cloud backend
				if (firebaseUser.email) {
					try {
						await syncUserToCloud(firebaseUser, pendingTeamNameRef.current);
					} catch (error) {
						console.error('Failed to sync user to cloud:', error);
					}
					pendingTeamNameRef.current = undefined;
				}
			} else {
				setUser(null);
				setFirebaseUser(null);
				setCloudToken(null);
				localStorage.removeItem(JWT_STORAGE_KEY);
			}
			setIsLoading(false);
		});

		return () => unsubscribe();
	}, []);

	const register = async (email: string, password: string, teamName?: string) => {
		try {
			if (teamName) {
				pendingTeamNameRef.current = teamName;
			}
			await signUpWithEmail(email, password);
		} catch (error: any) {
			pendingTeamNameRef.current = undefined;
			throw new Error(error.message || 'Registration failed');
		}
	};

	const login = async (email: string, password: string) => {
		try {
			await signInWithEmail(email, password);
		} catch (error: any) {
			throw new Error(error.message || 'Login failed');
		}
	};

	const loginWithGoogle = async () => {
		// signInWithGoogle tries popup first; if popup succeeds the returned
		// user triggers onAuthStateChanged automatically. If popup is blocked
		// it falls back to redirect (page navigates away).
		await signInWithGoogle();
	};

	const loginWithApple = async () => {
		await signInWithApple();
	};

	// Direct Apple auth for web — bypasses Firebase auth (audience mismatch)
	// and syncs Apple user directly to the cloud API
	const loginWithAppleToken = async (appleUser: { sub: string; email: string; name?: string; idToken: string }) => {
		const response = await fetch(`${API_URL}/api/v1/auth/apple-sync`, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({
				id_token: appleUser.idToken,
				email: appleUser.email,
				display_name: appleUser.name || undefined,
			}),
		});

		if (!response.ok) {
			const text = await response.text();
			throw new Error(`Apple sync failed (${response.status}): ${text}`);
		}

		const data = await response.json();
		if (data.token) {
			localStorage.setItem(JWT_STORAGE_KEY, data.token);
			setCloudToken(data.token);
		}

		setUser({
			uid: `apple:${appleUser.sub}`,
			email: appleUser.email,
			displayName: appleUser.name || null,
			photoURL: null,
		});
	};

	const resetPassword = async (email: string) => {
		await firebaseResetPassword(email);
	};

	const logout = async () => {
		try {
			await logOut();
		} catch (error: any) {
			throw new Error(error.message || 'Logout failed');
		}
	};

	const value: AuthContextType = {
		user,
		firebaseUser,
		cloudToken,
		login,
		loginWithGoogle,
		loginWithApple,
		loginWithAppleToken,
		register,
		resetPassword,
		logout,
		isAuthenticated: !!user,
		isLoading,
	};

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
	const context = useContext(AuthContext);
	if (context === undefined) {
		throw new Error('useAuth must be used within an AuthProvider');
	}
	return context;
}
